import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import Question from '../../models/question'
import { LETTER_FORMAT } from '../../utils/constants'
import { questionServiceParser, answerItemBodyParser } from '../../../../helpers/question-parser'
import Skill from '../../models/skill'
import SkillItem from '../../models/skill_item'
import { map } from 'lodash'
import markdownParser from '../../../../helpers/markdown-parser'
import { createTooltipText } from '../../utils/tooltip'
import UserExam from '../../models/user_exam'
import Exam from '../../models/exam'
import {
  resetStyleMathLive,
  checkMathLiveExist,
  createMathliveField
} from '../../../../lib/mathlive'
import SchoolSetting from '../../../api/v2/models/school_setting'
import { ExamQScoreHistory } from '../../../api/v2/models/q_score_history'
import { format } from 'date-fns'
import { processAudioSpeak } from '../../../../lib/speak_polly'

export interface ExamResultQuestionBodyProps {
  groupIndex: number
  currentQuestion: Question
  index: number
  schoolSetting: SchoolSetting
  exam: Exam
  userExam: UserExam
  isMultipleQuestion?: boolean
  examQScoreHistory?: ExamQScoreHistory | null
}

export function useExamResultQuestionBody(props: ExamResultQuestionBodyProps) {
  // Reactive Data
  const show = ref(false)

  // Computed
  const qScoreHistorySummary = computed(() => {
    if (!props.examQScoreHistory) {
      return null
    }
    return props.examQScoreHistory.getQScoreHistorySummary(props.currentQuestion.id)
  })

  const userScore = computed(() => props.currentQuestion.userScore)

  const maxScore = computed(() => props.currentQuestion.maxScore)

  const score = computed(() => props.currentQuestion.getScoreByUserExam(props.userExam))

  const isPass = computed(() => score.value === props.currentQuestion.maxScore)

  const isNotFullScore = computed(() => score.value < props.currentQuestion.maxScore && score.value > 0)

  const isNotPass = computed(() => score.value === 0)

  const explanation = computed(() => {
    return !props.currentQuestion.explanation ? '' : markdownParser(props.currentQuestion.explanation)
  })

  const value = computed<string[]>(() => {
    return props.userExam.finishedAt
      ? props.currentQuestion.getAnswerValue(props.exam.answersFlowQuestion(props.userExam))
      : props.currentQuestion.answerValues
  })

  const content = computed(() => {
    if (!props.currentQuestion) {
      return ''
    }

    setTimeout(() => {
      if (props.currentQuestion.isTextfield) {
        const element = document.getElementById(`question_${props.currentQuestion.id}`)
        if (element) {
          (element.querySelectorAll('.textfield-question') as any).forEach((el: HTMLInputElement, index: number) => {
            el.value = value.value[index] || ''

            const answerItem = props.currentQuestion.answerItems[index]

            let neoElement = null
            if (checkMathLiveExist(answerItem.body)) {
              neoElement = createMathliveField(el, false)
              neoElement.parentElement.parentElement.style.paddingBottom = '18px'
            } else {
              neoElement = el
            }

            setTimeout(() => {
              const htmlText = answerItem ? answerItemBodyParser(answerItem.body) : ''
              const resultClass =
                answerItem && answerItem.correctValue(el.value, props.currentQuestion.isTextfield)
                  ? 'right'
                  : 'wrong'
              createTooltipText(htmlText, neoElement, `textfield-question-answer ${resultClass}`)
            }, 100)
          })
        }
      }

      if (props.currentQuestion.isInlineSelect) {
        const element = document.getElementById(`question_${props.currentQuestion.id}`)
        if (element) {
          (element.querySelectorAll('.inline-select-question') as any).forEach((el: HTMLSelectElement, index: number) => {
            Array.prototype.forEach.call(el.options, (option: HTMLOptionElement) => {
              if (option.value === value.value[index]) {
                option.selected = true
              }

              const answerItems = props.currentQuestion.answerItemScopes[index]
              if (answerItems) {
                const correctAnswerItems = answerItems.filter((answerItem: any) => answerItem.correct)
                const htmlText = correctAnswerItems.map((answerItem: any) => answerItemBodyParser(answerItem.body)).join(', ')
                const resultClass =
                  correctAnswerItems.length &&
                    correctAnswerItems.every((answerItem: any) => answerItem.body !== value.value[index])
                    ? 'wrong'
                    : 'right'
                createTooltipText(htmlText, el, `textfield-question-answer ${resultClass}`)
              }
            })
          })
        }
      }

      // Text - Speak 実行
      // Lazy mode (loadWhenPlay=true): same iOS-unlock / media-element-limit fix as the test body.
      const questionElement = document.getElementById(`question_${props.currentQuestion.id}`)
      if (questionElement) {
        processAudioSpeak(questionElement, true)
      }
    }, 100)

    const contentStr = props.currentQuestion.body.split('<a ').join('<a target="_blank" ')
    return questionServiceParser(contentStr, props.currentQuestion, true)
  })

  // Functions
  function dateFormat(date: Date | string): string {
    const d = new Date(date)
    const year = d.getFullYear()
    const month = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${year}/${month}/${day}`
  }

  function getRateScore(scoreVal: number, maxScoreVal: number): number {
    if (maxScoreVal === 0) {
      return 0
    }
    return Math.round(scoreVal * 100 / maxScoreVal)
  }

  function getNameByExamType(type: string | number): string {
    const typeStr = String(type)
    switch (typeStr) {
      case String(Exam.TYPE_USE_NIGATE_EXAM):
        return `ニガテ質問を再テスト`
      case String(Exam.TYPE_USE_SPECIAL_EXAM):
        return `別質問を再テスト`
      case String(Exam.TYPE_USE_EXAM_FROM_FAILED):
        return `間違った質問を再テスト`
      default:
        return `同じ質問再テスト`
    }
  }

  function skillItemsName(skill: Skill): string {
    const skillItems = skill.skillItems.filter((skillItem) =>
      props.currentQuestion.skillItems.some((item) => item.id === skillItem.id)
    )
    return getSkillsName(skillItems)
  }

  function getSkillsName(skills: Skill[] | SkillItem[]): string {
    return map(skills, 'name').join(', ')
  }

  // Lifecycle
  function initShow() {
    show.value = false
    setTimeout(() => {
      show.value = true
    }, 100)

    resetStyleMathLive()
  }

  return {
    // Data
    show,
    LETTER_FORMAT,
    markdownParser,
    // Computed
    qScoreHistorySummary,
    userScore,
    maxScore,
    score,
    isPass,
    isNotFullScore,
    isNotPass,
    explanation,
    value,
    content,
    // Functions
    dateFormat,
    getRateScore,
    getNameByExamType,
    skillItemsName,
    getSkillsName,
    initShow
  }
}
